import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi, usersApi, cannedRepliesApi, attachmentsApi, categoriesApi } from '../lib/api-hooks';
import type { TicketListResponse } from '../lib/api-hooks';
import { useAuth } from '../store/auth';
import { StatusBadge, StatusSelector } from '../components/ticket/StatusBadge';
import { PriorityBadge, Avatar } from '../components/ticket/Avatar';
import { useToast } from '../components/ui/Toast';
import { formatSakNr, formatDateTime } from '../lib/format';
import type { TicketStatus, Message, CannedReply, Ticket } from '../lib/types';
import { getOriginalMessage, sanitizeEmailHtml } from '../lib/ticket-content';
import { clsx } from 'clsx';
import { renderedWatermark } from './inbox-helpers';

// ---------- Konstanter ----------
const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const STATUS_SHORTCUTS: Record<string, TicketStatus> = {
  '1': 'NY',
  '2': 'UNDER_ARBEID',
  '3': 'VENTER_KUNDE',
  '4': 'VENTER_INTERNT',
  '5': 'LOST',
};

const STATUS_LABELS: Record<TicketStatus, string> = {
  NY: 'Ny',
  UNDER_ARBEID: 'Under arbeid',
  VENTER_KUNDE: 'Venter kunde',
  VENTER_INTERNT: 'Venter internt',
  LOST: 'Løst',
  LUKKET: 'Lukket',
};

type ReplyMode = 'none' | 'customer' | 'internal';

export interface DraftState {
  html: string;
  text: string;
  files: File[];
  attachmentIds: string[]; // IDer for opplastede vedlegg (kobles til meldingen ved send)
}

const emptyDraft = (): DraftState => ({ html: '', text: '', files: [], attachmentIds: [] });

// ---------- Hovedkomponent ----------
export default function TicketDetailPage({
  embedded,
  backTo,
  draftCache,
  onComposeChange,
}: {
  embedded?: boolean;
  backTo?: string;
  draftCache?: Map<string, Record<'customer' | 'internal', DraftState>>;
  onComposeChange?: (busy: boolean) => void;
}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();

  const [replyMode, setReplyMode] = useState<ReplyMode>('none');
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  // Separate utkast for kunde-svar og internt notat, slik at man ikke mister tekst ved å bytte modus.
  const [drafts, setDrafts] = useState<Record<'customer' | 'internal', DraftState>>(
    () =>
      (id ? draftCache?.get(id) : undefined) ?? { customer: emptyDraft(), internal: emptyDraft() },
  );
  const [pendingDiscard, setPendingDiscard] = useState(false);

  const { data: ticket, dataUpdatedAt, isLoading, isError: ticketError } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => ticketsApi.get(id!),
    enabled: !!id,
    refetchInterval: 15_000,
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list() as Promise<Array<{ id: string; navn: string; epost: string; rolle: string; aktiv: boolean }>>,
  });

  const { data: cannedReplies } = useQuery({
    queryKey: ['canned-replies'],
    queryFn: () => cannedRepliesApi.list() as Promise<CannedReply[]>,
  });

  const { data: categories } = useQuery({
    queryKey: ['categories-active'],
    queryFn: categoriesApi.listActive,
  });

  // Vannmerke = nyeste rendrede meldingstidspunkt (eller sakens opprettet-dato).
  // Beregnes fra faktisk lastede meldinger, ikke sistAktivitet, slik at meldinger
  // som ikke er hentet ennå ikke markeres som lest.
  const renderedThrough = useMemo(
    () => (ticket && ticket.id === id ? renderedWatermark(ticket) : null),
    [ticket, id],
  );

  // Vannmerker spores per bruker+sak slik at bytte til en annen sak på
  // standalone-ruten ikke gjenbruker et eldre vannmerke.
  const markReadSentRef = useRef(new Map<string, number>());
  const markReadInFlightRef = useRef(new Map<string, number>());
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Marker saken som lest når den åpnes – og på nytt hvis den får ny
  // aktivitet mens den vises. Oppdaterer alle bufrede lister lokalt
  // slik at ulest-prikken forsvinner uten ekstra henting.
  useEffect(() => {
    if (!id || renderedThrough == null) return;

    const attempt = () => {
      if (document.visibilityState !== 'visible') return;
      const watermark = renderedThrough;
      const key = `${user?.id}:${id}`;
      const sent = markReadSentRef.current.get(key) ?? 0;
      const inFlight = markReadInFlightRef.current.get(key) ?? 0;
      if (watermark <= sent || watermark <= inFlight) return;
      markReadInFlightRef.current.set(key, watermark);
      const capturedId = id;
      ticketsApi
        .markRead(capturedId, new Date(watermark).toISOString())
        .then((res) => {
          markReadSentRef.current.set(
            key,
            Math.max(markReadSentRef.current.get(key) ?? 0, watermark),
          );
          if (!mountedRef.current) return;
          qc.setQueriesData<TicketListResponse>({ queryKey: ['tickets'] }, (old) =>
            old?.tickets
              ? {
                  ...old,
                  tickets: old.tickets.map((t) =>
                    t.id === capturedId && Date.parse(t.sistAktivitet) <= Date.parse(res.lestTil)
                      ? { ...t, ulest: false }
                      : t,
                  ),
                }
              : old,
          );
        })
        .catch(() => {
          // Neste polling-oppdatering forsøker på nytt
        })
        .finally(() => {
          if (markReadInFlightRef.current.get(key) === watermark) {
            markReadInFlightRef.current.delete(key);
          }
        });
    };

    attempt();
    const onVisible = () => attempt();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // dataUpdatedAt er med slik at et feilet markRead-forsøk prøves på nytt
    // ved neste polling-oppdatering (vannmerket kan være uendret).
  }, [id, renderedThrough, qc, user?.id, dataUpdatedAt]);

  // ---------- Mutasjoner: alle med optimistisk oppdatering + rollback ved feil ----------

  const updateStatus = useMutation({
    mutationFn: (status: TicketStatus) => ticketsApi.update(id!, { status }),
    onMutate: async (status) => {
      await qc.cancelQueries({ queryKey: ['ticket', id] });
      const previous = qc.getQueryData<Ticket>(['ticket', id]);
      qc.setQueryData<Ticket>(['ticket', id], (old) => (old ? { ...old, status } : old));
      return { previous };
    },
    onError: (_err, _status, context) => {
      if (context?.previous) qc.setQueryData(['ticket', id], context.previous);
      toast.error('Kunne ikke oppdatere status. Prøv igjen.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['ticket', id] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['kanban-tickets'] });
    },
  });

  // Endrer status og tilbyr angre for endringer det er lett å gjøre ved et uhell.
  const changeStatus = useCallback(
    (status: TicketStatus, opts?: { silent?: boolean }) => {
      const previousStatus = ticket?.status;
      updateStatus.mutate(status, {
        onSuccess: () => {
          if (!opts?.silent && previousStatus && previousStatus !== status) {
            toast.show({
              message: `Status endret til «${STATUS_LABELS[status]}».`,
              variant: 'success',
              actionLabel: 'Angre',
              onAction: () => updateStatus.mutate(previousStatus),
            });
          }
        },
      });
    },
    [ticket?.status, toast, updateStatus],
  );

  const selfAssign = useMutation({
    mutationFn: () => ticketsApi.selfAssign(id!),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['ticket', id] });
      const previous = qc.getQueryData<Ticket>(['ticket', id]);
      qc.setQueryData<Ticket>(['ticket', id], (old) => (old && user ? { ...old, assigneeId: user.id } : old));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(['ticket', id], context.previous);
      toast.error('Kunne ikke tildele saken til deg. Prøv igjen.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['ticket', id] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  const assignTo = useMutation({
    mutationFn: (assigneeId: string | null) => ticketsApi.assign(id!, assigneeId),
    onMutate: async (assigneeId) => {
      await qc.cancelQueries({ queryKey: ['ticket', id] });
      const previous = qc.getQueryData<Ticket>(['ticket', id]);
      qc.setQueryData<Ticket>(['ticket', id], (old) => (old ? { ...old, assigneeId } : old));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(['ticket', id], context.previous);
      toast.error('Kunne ikke endre tildeling. Prøv igjen.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['ticket', id] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  const deleteTicket = useMutation({
    mutationFn: () => ticketsApi.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      toast.success('Sak slettet.');
      navigate(backTo ?? '/inbox');
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Kunne ikke slette saken.');
    },
  });

  const updateCategory = useMutation({
    mutationFn: (kategori: string | null) => ticketsApi.update(id!, { kategori }),
    onMutate: async (kategori) => {
      await qc.cancelQueries({ queryKey: ['ticket', id] });
      const previous = qc.getQueryData<Ticket>(['ticket', id]);
      qc.setQueryData<Ticket>(['ticket', id], (old) => (old ? { ...old, kategori } : old));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(['ticket', id], context.previous);
      toast.error('Kunne ikke oppdatere sakstype. Prøv igjen.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['ticket', id] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  const sendMessage = useMutation({
    mutationFn: (data: { type: 'KUNDE_SVAR' | 'INTERNT_NOTAT'; innholdHtml: string; innholdTekst?: string; attachmentIds?: string[] }) =>
      ticketsApi.createMessage(id!, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['ticket', id] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      const mode: 'customer' | 'internal' = variables.type === 'KUNDE_SVAR' ? 'customer' : 'internal';
      setDrafts((prev) => {
        const next = { ...prev, [mode]: emptyDraft() };
        if (id && draftCache) draftCache.set(id, next);
        return next;
      });
      setReplyMode('none');
      toast.success(mode === 'customer' ? 'Svar sendt til kunde.' : 'Internt notat lagret.');
    },
    onError: () => {
      toast.error('Meldingen ble ikke sendt. Sjekk tilkoblingen og prøv igjen.');
    },
  });

  // ---------- Utkast-håndtering ----------
  const currentMode = replyMode === 'none' ? null : replyMode;
  const currentDraft = currentMode ? drafts[currentMode] : emptyDraft();
  const hasDraftContent = currentDraft.text.trim().length > 0 || currentDraft.files.length > 0;

  const setDraftFor = useCallback(
    (mode: 'customer' | 'internal', patch: Partial<DraftState>) => {
      setDrafts((prev) => {
        const next = { ...prev, [mode]: { ...prev[mode], ...patch } };
        if (id && draftCache) draftCache.set(id, next);
        return next;
      });
    },
    [draftCache, id],
  );

  const requestCloseReply = useCallback(() => {
    if (hasDraftContent && !pendingDiscard) {
      setPendingDiscard(true);
      return;
    }
    setPendingDiscard(false);
    setReplyMode('none');
  }, [hasDraftContent, pendingDiscard]);

  const openReplyMode = useCallback((mode: 'customer' | 'internal') => {
    setPendingDiscard(false);
    setReplyMode(mode);
  }, []);

  // Snarvei-innsetting av hurtigsvar (brukt både fra autocomplete og fra klikk på chip)
  const applyCannedReply = useCallback(
    (mode: 'customer' | 'internal', snarvei: string) => {
      const found = cannedReplies?.find((c) => c.snarvei === snarvei);
      if (!found) return;
      setDraftFor(mode, {
        html: drafts[mode].html + found.innhold,
        text: drafts[mode].text + found.innhold,
      });
    },
    [cannedReplies, drafts, setDraftFor],
  );

  // ---------- Globale tastatursnarveier ----------
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target;
      const isFormField =
        target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
      if (isFormField) return;
      if (!ticket) return;

      if (e.key === '?') {
        e.preventDefault();
        setShowShortcutHelp((v) => !v);
        return;
      }

      // Mens svar-boksen er åpen skal kun Escape gjelde – alt annet er trolig utilsiktet.
      if (replyMode !== 'none') {
        if (e.key === 'Escape') requestCloseReply();
        return;
      }

      switch (e.key) {
        case 'r':
          e.preventDefault();
          openReplyMode('customer');
          break;
        case 'n':
          e.preventDefault();
          openReplyMode('internal');
          break;
        case 'a':
          e.preventDefault();
          if (ticket.assigneeId !== user?.id) selfAssign.mutate();
          break;
        case 'Escape':
          setShowShortcutHelp(false);
          break;
        default: {
          const status = STATUS_SHORTCUTS[e.key];
          if (status) {
            e.preventDefault();
            changeStatus(status);
          }
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [ticket, user?.id, replyMode, changeStatus, openReplyMode, requestCloseReply, selfAssign]);

  // Advar ved navigering bort fra fullsiden hvis det finnes usendte utkast
  // (vedlegg kan ikke gjenopprettes etter reload).
  useEffect(() => {
    if (embedded) return;
    const handler = (e: BeforeUnloadEvent) => {
      const dirty =
        drafts.customer.text.trim().length > 0 ||
        drafts.customer.files.length > 0 ||
        drafts.internal.text.trim().length > 0 ||
        drafts.internal.files.length > 0;
      if (dirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [embedded, drafts]);

  // Rapporter til forelderen (innboks-listen) når svar-boks eller modal er åpen,
  // slik at liste-snarveier ikke stjeler tastetrykk.
  useEffect(() => {
    onComposeChange?.(replyMode !== 'none' || showShortcutHelp);
    return () => onComposeChange?.(false);
  }, [replyMode, showShortcutHelp, onComposeChange]);

  if (isLoading) {
    return <TicketDetailSkeleton />;
  }

  if (ticketError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-500">
        <p>Klarte ikke å laste saken.</p>
        <button onClick={() => qc.invalidateQueries({ queryKey: ['ticket', id] })} className="btn-secondary text-sm">
          Prøv igjen
        </button>
        {backTo && (
          <button onClick={() => navigate(backTo)} className="btn-ghost text-sm">
            Tilbake til listen
          </button>
        )}
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-500">
        <p>Sak ikke funnet.</p>
        {backTo && (
          <button onClick={() => navigate(backTo)} className="btn-ghost text-sm">
            Tilbake til listen
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={
        embedded
          ? 'mx-auto flex h-full min-h-0 w-full max-w-5xl flex-col'
          : 'mx-auto flex h-[calc(100vh-0px)] max-w-5xl flex-col md:h-[calc(100vh-49px)]'
      }
    >
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-4 py-3 md:px-6 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-start gap-3">
          <button
            onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
            className="btn-ghost p-1"
            title="Tilbake"
            aria-label="Tilbake til saksliste"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm text-slate-400">{formatSakNr(ticket.sakNr)}</span>
              <StatusBadge status={ticket.status} size="md" />
              <PriorityBadge prioritet={ticket.prioritet} />
            </div>
            <h1 className="mt-2 break-words text-lg font-semibold text-slate-800 dark:text-slate-100">{ticket.tittel}</h1>
            {ticket.customer && (
              <p className="mt-1 text-sm text-slate-500">
                {ticket.customer.navn || ticket.customer.epost}
                {ticket.customer.navn && ` · ${ticket.customer.epost}`}
              </p>
            )}
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <select
            value={categories?.find((c) => c.navn === ticket.kategori)?.id ?? ''}
            onChange={(e) => {
              const cat = categories?.find((c) => c.id === e.target.value);
              updateCategory.mutate(cat?.navn ?? null);
            }}
            disabled={updateCategory.isPending}
            className="select-input ml-1 min-w-[120px] text-xs disabled:opacity-60"
            title="Sakstype"
            aria-label="Sakstype"
          >
            <option value="">Ingen type</option>
            {categories?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.navn}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowShortcutHelp(true)}
            className="btn-ghost ml-auto p-1 text-xs text-slate-400"
            title="Vis tastatursnarveier (?)"
            aria-label="Vis tastatursnarveier"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <path d="M12 17h.01" />
            </svg>
          </button>
          </div>
        </div>
      </div>

      {/* Actions-bar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2 md:px-6">
        <button onClick={() => openReplyMode('customer')} className="btn-primary text-sm" title="Svar kunde (r)">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Svar kunde
          {drafts.customer.text.trim() && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-white/80" aria-hidden />}
        </button>
        <button onClick={() => openReplyMode('internal')} className="btn-secondary text-sm" title="Internt notat (n)">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
          Intern notat
          {drafts.internal.text.trim() && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />}
        </button>
        <button
          onClick={() => selfAssign.mutate()}
          className="btn-ghost text-sm"
          title="Ta sak selv (a)"
          disabled={ticket.assigneeId === user?.id || selfAssign.isPending}
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          {ticket.assigneeId === user?.id ? 'Min sak' : 'Ta selv'}
        </button>

        <div className="relative ml-auto">
          <select
            value={ticket.assigneeId ?? ''}
            onChange={(e) => assignTo.mutate(e.target.value || null)}
            disabled={assignTo.isPending}
            className="select-input min-w-[150px] text-sm disabled:opacity-60"
            aria-label="Tildel sak til"
          >
            <option value="">Ufordelt</option>
            {users?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.navn}
              </option>
            ))}
          </select>
        </div>
        {user?.rolle === 'ADMIN' && (
          <button
            onClick={() => {
              if (confirm(`Slett SAK-${String(ticket.sakNr).padStart(4, '0')} "${ticket.tittel}"?\n\nDette kan ikke angres. Alle meldinger og vedlegg slettes permanent.`)) {
                deleteTicket.mutate();
              }
            }}
            disabled={deleteTicket.isPending}
            className="btn-ghost text-sm text-red-600 hover:bg-red-50 hover:text-red-700"
            title="Slett sak permanent (kun admin)"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            {deleteTicket.isPending ? 'Sletter…' : 'Slett sak'}
          </button>
        )}
      </div>

      {/* Status-segmented control */}
      <div className="border-b border-slate-200 bg-white px-4 py-2 md:px-6">
        <StatusSelector current={ticket.status} onChange={(s) => changeStatus(s)} />
      </div>

      {/* Beskrivelse / opprinnelig henvendelse */}
      <TicketDescription ticket={ticket} />

      {/* Meldinger */}
      <MessageThread messages={ticket.messages ?? []} />

      {/* Svar-boks */}
      {replyMode !== 'none' && (
        <ReplyBox
          mode={replyMode}
          ticketId={id!}
          draft={currentDraft}
          onChangeDraft={(patch) => setDraftFor(replyMode, patch)}
          pendingDiscard={pendingDiscard}
          onCancel={requestCloseReply}
          onKeepEditing={() => setPendingDiscard(false)}
          onSend={(andClose, attachmentIds) => {
            const mode = replyMode;
            sendMessage.mutate(
              {
                type: mode === 'customer' ? 'KUNDE_SVAR' : 'INTERNT_NOTAT',
                innholdHtml: drafts[mode].html,
                innholdTekst: drafts[mode].text,
                attachmentIds: attachmentIds && attachmentIds.length > 0 ? attachmentIds : undefined,
              },
              {
                onSuccess: () => {
                  if (andClose) changeStatus('LOST', { silent: true });
                },
              },
            );
          }}
          onCannedReply={(snarvei) => applyCannedReply(replyMode, snarvei)}
          cannedReplies={cannedReplies ?? []}
          sending={sendMessage.isPending}
          toast={toast}
        />
      )}

      {showShortcutHelp && <ShortcutHelpOverlay onClose={() => setShowShortcutHelp(false)} />}
    </div>
  );
}

// ---------- Skjelett-lasting ----------
function TicketDetailSkeleton() {
  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col gap-4 p-6" aria-busy="true" aria-label="Laster sak…">
      <div className="h-4 w-40 animate-pulse rounded bg-slate-100" />
      <div className="h-7 w-2/3 animate-pulse rounded bg-slate-100" />
      <div className="h-24 w-full animate-pulse rounded-lg bg-slate-100" />
      <div className="h-20 w-full animate-pulse rounded-lg bg-slate-100" />
      <div className="h-20 w-3/4 animate-pulse rounded-lg bg-slate-100" />
    </div>
  );
}

// ---------- Hjelp-overlay for tastatursnarveier ----------
function ShortcutHelpOverlay({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const rows: Array<[string, string]> = [
    ['r', 'Svar kunde'],
    ['n', 'Internt notat'],
    ['a', 'Ta saken selv'],
    ['1 – 5', 'Sett status'],
    ['Esc', 'Lukk / avbryt'],
    ['Ctrl/⌘ + Enter', 'Send melding'],
    ['/hurtigsvar', 'Sett inn hurtigsvar'],
    ['?', 'Vis / skjul denne hjelpen'],
  ];

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Tastatursnarveier"
      onClick={onClose}
    >
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-slate-800" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Tastatursnarveier</h2>
          <button onClick={onClose} className="btn-ghost p-1" aria-label="Lukk">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <dl className="space-y-1.5">
          {rows.map(([key, desc]) => (
            <div key={key} className="flex items-center justify-between text-sm">
              <dt className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">{key}</dt>
              <dd className="text-slate-600 dark:text-slate-300">{desc}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

// ---------- Hjelpe: formater filstørrelse ----------
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------- Vedleggsliste med sikker nedlasting ----------
function AttachmentList({ ticketId, attachments }: { ticketId: string; attachments: Array<{ id: string; filnavn: string; mimeType: string; storrelse: number }> }) {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [blobUrls, setBlobUrls] = useState<Record<string, string>>({});

  const handleDownload = async (e: React.MouseEvent, attachmentId: string) => {
    e.preventDefault();
    if (blobUrls[attachmentId]) {
      window.open(blobUrls[attachmentId], '_blank');
      return;
    }
    setDownloading(attachmentId);
    try {
      const blobUrl = await attachmentsApi.download(ticketId, attachmentId);
      setBlobUrls((prev) => ({ ...prev, [attachmentId]: blobUrl }));
      window.open(blobUrl, '_blank');
    } catch (err) {
      console.error('Nedlasting feilet:', err);
    } finally {
      setDownloading(null);
    }
  };

  const images = attachments.filter((a) => a.mimeType.startsWith('image/'));

  return (
    <div className="mt-3 space-y-2">
      {/* Bilde-preview */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((a) => (
            <button
              key={a.id}
              onClick={(e) => handleDownload(e, a.id)}
              className="block overflow-hidden rounded-lg border border-slate-200 transition-transform hover:scale-105 dark:border-slate-700"
              title={a.filnavn}
            >
              {blobUrls[a.id] ? (
                <img src={blobUrls[a.id]} alt={a.filnavn} className="h-24 w-24 object-cover" />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center bg-slate-100 text-slate-400">
                  {downloading === a.id ? (
                    <span className="text-xs">Laster…</span>
                  ) : (
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="9" cy="9" r="2" />
                      <path d="m21 15-3.5-3.5L9 20" />
                    </svg>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      {/* Fil-liste */}
      <div className="flex flex-wrap gap-2">
        {attachments.map((a) => (
          <button
            key={a.id}
            onClick={(e) => handleDownload(e, a.id)}
            disabled={downloading === a.id}
            className="chip bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
          >
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
            {downloading === a.id ? 'Laster…' : a.filnavn}
            <span className="ml-1 text-xs text-slate-400">({formatFileSize(a.storrelse)})</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- Beskrivelse / opprinnelig henvendelse ----------
function TicketDescription({ ticket }: { ticket: Ticket }) {
  const originalMessage = getOriginalMessage(ticket);
  const rawHtml = originalMessage?.innholdHtml || undefined;
  const text = originalMessage?.innholdTekst || ticket.beskrivelse || '';
  const safeHtml = useMemo(() => (rawHtml ? sanitizeEmailHtml(rawHtml) : undefined), [rawHtml]);

  if (!text && !safeHtml) return null;

  return (
    <div className="border-b border-slate-200 bg-slate-50/50 px-4 py-4 md:px-6 dark:border-slate-800 dark:bg-slate-900/30">
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <svg className="h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Opprinnelig henvendelse</span>
        <span className="text-xs text-slate-400">· Fra kunde{originalMessage?.fromEmail ? ` · ${originalMessage.fromEmail}` : ''}{originalMessage?.opprettet ? ` · ${formatDateTime(originalMessage.opprettet)}` : ''}</span>
      </div>
      {safeHtml ? (
        <div className="email-rich-content text-sm text-slate-700 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: safeHtml }} />
      ) : (
        <p className="whitespace-pre-wrap break-words text-sm text-slate-700 dark:text-slate-300">{text}</p>
      )}
    </div>
  );
}

// ---------- Meldingstråd ----------
function MessageThread({ messages }: { messages: Message[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(messages.length);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const grew = messages.length > prevCountRef.current;
    prevCountRef.current = messages.length;
    if (!grew) return;

    // Ikke ta over scroll-posisjonen hvis brukeren aktivt leser tidligere historikk.
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
    if (nearBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages.length]);

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-4 md:px-6">
      {messages.length === 0 ? (
        <div className="flex h-full items-center justify-center text-sm text-slate-400">Ingen meldinger i denne saken ennå.</div>
      ) : (
        <div className="space-y-4">
          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isInternal = message.type === 'INTERNT_NOTAT';
  const isCustomer = message.type === 'KUNDE_MELDING';
  const isSystem = message.type === 'SYSTEM';
  const isOutgoing = message.type === 'KUNDE_SVAR';
  const safeHtml = useMemo(
    () => (message.innholdHtml ? sanitizeEmailHtml(message.innholdHtml) : undefined),
    [message.innholdHtml],
  );

  if (isSystem) {
    return (
      <div className="flex justify-center">
        <span className="chip bg-slate-100 text-slate-400">{message.innholdTekst}</span>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'rounded-lg p-4',
        isInternal && 'bg-amber-50/70 border border-amber-200/80 dark:bg-amber-500/5 dark:border-amber-500/20',
        isCustomer && 'bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700',
        isOutgoing && 'bg-brand-50/70 border border-brand-200/80 dark:bg-brand-500/5 dark:border-brand-500/20',
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        {message.author && <Avatar navn={message.author.navn} avatarUrl={message.author.avatarUrl} size="sm" />}
        <div className="flex-1">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
            {isCustomer ? message.fromEmail || 'Kunde' : message.author?.navn || 'Ukjent'}
          </span>
          {isCustomer && <span className="ml-2 chip bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">Fra kunde</span>}
          {isInternal && <span className="ml-2 chip bg-amber-200/70 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">Internt notat</span>}
          {isOutgoing && <span className="ml-2 chip bg-brand-200/70 text-brand-800 dark:bg-brand-500/20 dark:text-brand-200">Svar til kunde</span>}
        </div>
        <span className="text-xs text-slate-400">{formatDateTime(message.opprettet)}</span>
      </div>

      {safeHtml ? (
        <div className="email-rich-content text-sm text-slate-700 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: safeHtml }} />
      ) : (
        <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{message.innholdTekst}</p>
      )}

      {message.attachments?.length ? (
        <AttachmentList ticketId={message.ticketId} attachments={message.attachments} />
      ) : null}
    </div>
  );
}

// ---------- Svar-boks ----------
interface ToastApi {
  error: (message: string, opts?: Record<string, unknown>) => void;
}

function ReplyBox({
  mode,
  ticketId,
  draft,
  onChangeDraft,
  pendingDiscard,
  onCancel,
  onKeepEditing,
  onSend,
  onCannedReply,
  cannedReplies,
  sending,
  toast,
}: {
  mode: 'customer' | 'internal';
  ticketId: string;
  draft: DraftState;
  onChangeDraft: (patch: Partial<DraftState>) => void;
  pendingDiscard: boolean;
  onCancel: () => void;
  onKeepEditing: () => void;
  onSend: (andClose: boolean, attachmentIds?: string[]) => void;
  onCannedReply: (snarvei: string) => void;
  cannedReplies: CannedReply[];
  sending: boolean;
  toast: ToastApi;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [suggestionIndex, setSuggestionIndex] = useState(0);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Hurtigsvar-forslag basert på det som står etter siste "/" i teksten.
  const slashMatch = draft.text.match(/\/(\w*)$/);
  const suggestions = useMemo(() => {
    if (!slashMatch) return [];
    const query = slashMatch[1].toLowerCase();
    return cannedReplies.filter((c) => c.snarvei.toLowerCase().startsWith(query)).slice(0, 6);
  }, [slashMatch, cannedReplies]);

  useEffect(() => {
    setSuggestionIndex(0);
  }, [suggestions.length, slashMatch?.[1]]);

  const selectSuggestion = (snarvei: string) => {
    const cleaned = draft.text.replace(/\/\w*$/, '');
    onChangeDraft({ text: cleaned, html: cleaned.replace(/\n/g, '<br>') });
    onCannedReply(snarvei);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    onChangeDraft({ text: val, html: val.replace(/\n/g, '<br>') });
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSuggestionIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSuggestionIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectSuggestion(suggestions[suggestionIndex].snarvei);
        return;
      }
      if (e.key === 'Escape') {
        // Lukk kun forslagslisten, ikke hele svar-boksen.
        e.stopPropagation();
        return;
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (canSend) handleSendWithAttachments(false);
    }
  };

  const validateFiles = (files: File[]): { valid: File[]; errors: string[] } => {
    const valid: File[] = [];
    const errors: string[] = [];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        errors.push(`${file.name} er større enn ${MAX_FILE_SIZE_MB} MB og ble ikke lagt til.`);
        continue;
      }
      if (file.size === 0) {
        errors.push(`${file.name} er en tom fil og ble ikke lagt til.`);
        continue;
      }
      valid.push(file);
    }
    return { valid, errors };
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const { valid, errors } = validateFiles(selected);
    if (valid.length > 0) onChangeDraft({ files: [...draft.files, ...valid] });
    setUploadError(errors.length > 0 ? errors.join(' ') : null);
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    onChangeDraft({ files: draft.files.filter((_, i) => i !== index) });
  };

  const handleSendWithAttachments = async (andClose: boolean) => {
    if (draft.files.length > 0) {
      setUploading(true);
      setUploadError(null);
      try {
        const uploadedIds: string[] = [];
        for (const file of draft.files) {
          const result = await attachmentsApi.upload(ticketId, file);
          if (result?.id) uploadedIds.push(result.id);
        }
        setUploading(false);
        onSend(andClose, [...draft.attachmentIds, ...uploadedIds]);
        return;
      } catch (err) {
        const message = (err as Error).message || 'Ukjent feil ved opplasting.';
        setUploadError(message);
        toast.error('Kunne ikke laste opp vedlegg. Meldingen ble ikke sendt.');
        setUploading(false);
        return;
      }
    }
    onSend(andClose, draft.attachmentIds.length > 0 ? draft.attachmentIds : undefined);
  };

  const isCustomer = mode === 'customer';
  const canSend = draft.text.trim().length > 0 && !sending && !uploading;

  return (
    <div
      className={clsx('border-t-2 p-4 md:px-6', isCustomer ? 'border-brand-300 bg-brand-50 dark:bg-brand-500/5' : 'border-amber-300 bg-amber-50 dark:bg-amber-500/5')}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className={clsx('chip', isCustomer ? 'bg-brand-200 text-brand-800' : 'bg-amber-200 text-amber-800')}>
          {isCustomer ? 'Svar til kunde' : 'Intern notat (sendes ikke til kunde)'}
        </span>
        {pendingDiscard ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-600 dark:text-slate-300">Forkaste utkastet?</span>
            <button onClick={onCancel} className="font-medium text-red-600 hover:underline">
              Forkast
            </button>
            <button onClick={onKeepEditing} className="font-medium text-slate-600 dark:text-slate-300 hover:underline">
              Fortsett å skrive
            </button>
          </div>
        ) : (
          <button onClick={onCancel} className="btn-ghost text-sm">
            Avbryt (Esc)
          </button>
        )}
      </div>

      <div className="relative">
        <textarea
          ref={textareaRef}
          value={draft.text}
          onChange={handleInputChange}
          onKeyDown={handleTextareaKeyDown}
          placeholder={
            isCustomer ? 'Skriv svar til kunde… (bruk / for hurtigsvar)' : 'Skriv internt notat… (bruk / for hurtigsvar)'
          }
          rows={5}
          className="input min-h-[100px] resize-y"
          aria-describedby="reply-hint"
        />

        {suggestions.length > 0 && (
          <div className="absolute bottom-full left-0 z-10 mb-1 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
            {suggestions.map((s, i) => (
              <button
                key={s.id ?? s.snarvei}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSuggestion(s.snarvei);
                }}
                className={clsx(
                  'flex w-full flex-col items-start px-3 py-2 text-left text-sm',
                  i === suggestionIndex ? 'bg-brand-50 text-brand-800 dark:bg-brand-500/10 dark:text-brand-300' : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700/50',
                )}
              >
                <span className="font-medium">/{s.snarvei}</span>
                <span className="truncate text-xs text-slate-400">{s.innhold}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <p id="reply-hint" className="mt-1 text-xs text-slate-400">
        Ctrl/⌘ + Enter for å sende
      </p>

      <div className="mt-2">
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
        <button type="button" onClick={() => fileInputRef.current?.click()} className="btn-ghost text-sm">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
          Legg ved fil
        </button>

        {draft.files.length > 0 && (
          <div className="mt-1 space-y-1">
            {draft.files.map((file, i) => (
              <div key={`${file.name}-${i}`} className="flex items-center justify-between rounded-lg bg-white/60 px-3 py-1.5 dark:bg-slate-800/60">
                <div className="flex items-center gap-2 truncate">
                  <svg className="h-4 w-4 shrink-0 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                  <span className="truncate text-sm text-slate-700 dark:text-slate-200">{file.name}</span>
                  <span className="shrink-0 text-xs text-slate-400">({(file.size / 1024).toFixed(0)} KB)</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-slate-400 hover:text-red-600"
                  aria-label={`Fjern ${file.name}`}
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {uploadError && <div className="mt-1 rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-400">{uploadError}</div>}
      </div>

      {cannedReplies.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          <span className="text-xs text-slate-400">Hurtigsvar:</span>
          {cannedReplies.map((c) => (
            <button
              key={c.id ?? c.snarvei}
              onClick={() => onCannedReply(c.snarvei)}
              className="chip bg-white text-slate-600 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              /{c.snarvei}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        {isCustomer && (
          <button
            onClick={() => handleSendWithAttachments(true)}
            className="btn-secondary text-sm"
            disabled={!canSend}
            title="Sender svaret og setter saken til Løst"
          >
            Send og løs sak
          </button>
        )}
        <button onClick={() => handleSendWithAttachments(false)} className="btn-primary text-sm" disabled={!canSend}>
          {uploading ? 'Laster opp filer…' : sending ? 'Sender…' : isCustomer ? 'Send svar' : 'Lagre notat'}
        </button>
      </div>
    </div>
  );
}
